import { useCallback, useEffect, useRef, useState } from 'react';
import { isPhysicallyIotBoard, readChipIdViaRom } from '@/hooks/feature/boards/useBoardWebSerialApiIdentify';
import { WebSerialApiUpload } from '@/hooks/feature/boards/useBoardWebSerialApiUpload';
import type { DetectedBoard } from '@/stores/useBoardEditorStore';

interface ESP32Artifact {
    filename: string;
    address: string;
    data_b64: string;
}

interface UseBoardWebSerialApiOptions {
    hasSelectedBoard: boolean;
    selectedBoard: 'orange' | 'blue';
    isMonitorOpen: boolean;
    isUploading: boolean;
    appendLog: (message: string) => void;
    appendErrorLog: (message: string) => void;
    onBoardsDetected: (boards: DetectedBoard[]) => void;
    onPortUpdate: (port: string | null) => void;
    getActiveRequestId: () => string | null;
    appendMonitorMessage: (message: string) => void;
    appendMonitorMessageBatch: (message: string[]) => void;
    onUploadEvent: (e: {
        source: 'webserialapi';
        kind: 'progress' | 'result';
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
    setHasSelectedBoard: (selected: boolean) => void;
    onIdentifyMismatch?: () => void;
    onChipIdResolved?: (chipId: number | null) => void;
}

export function useBoardWebSerialApi(options: UseBoardWebSerialApiOptions) {
    const [port, setPort] = useState<any | null>(null);
    // chip_id取得中(自動リトライ込み)かどうか。UI側でローディング表示を出すためだけの状態。
    const [isChipIdProbing, setIsChipIdProbing] = useState(false);
    const readerRef = useRef<any>(null);
    const portRef = useRef<any>(null);
    const baudRateRef = useRef<number>(9600);
    const {
        hasSelectedBoard,
        selectedBoard,
        isMonitorOpen,
        isUploading,
        appendLog,
        appendErrorLog,
        onBoardsDetected,
        onPortUpdate,
        onUploadEvent,
        appendMonitorMessage,
        appendMonitorMessageBatch,
        setHasSelectedBoard,
        onIdentifyMismatch,
        onChipIdResolved,
    } = options;
    const hasSelectedBoardRef = useRef(hasSelectedBoard);
    const selectedBoardRef = useRef<'orange' | 'blue'>(selectedBoard);
    const appendLogRef = useRef(appendLog);
    const appendErrorLogRef = useRef(appendErrorLog);
    const onBoardsDetectedRef = useRef(onBoardsDetected);
    const onPortUpdateRef = useRef(onPortUpdate);
    const onUploadEventRef = useRef(onUploadEvent);
    const appendMonitorMessageRef = useRef(appendMonitorMessage);
    const appendMonitorMessageBatchRef = useRef(appendMonitorMessageBatch);
    const setHasSelectedBoardRef = useRef(setHasSelectedBoard);
    const onIdentifyMismatchRef = useRef(onIdentifyMismatch);
    const onChipIdResolvedRef = useRef(onChipIdResolved);

    const bcRef = useRef<BroadcastChannel | null>(null);
    const portLockRef = useRef<Promise<void>>(Promise.resolve());
    // モニター読み取りループの生存フラグ。以前はeffect内のローカル変数だったため、disconnect()から
    // 止める手段が無く、ボード切り替え時にreaderをcancelしても読み取りループが同じポートに対して
    // 新しいreaderを取り直そうとし、port.close()と競合してタブごと固まる不具合があった。
    const isMonitorReadingRef = useRef(false);

    useEffect(() => {
        appendLogRef.current = appendLog;
        appendErrorLogRef.current = appendErrorLog;
        onBoardsDetectedRef.current = onBoardsDetected;
        onPortUpdateRef.current = onPortUpdate;
        onUploadEventRef.current = onUploadEvent;
        appendMonitorMessageRef.current = appendMonitorMessage;
        appendMonitorMessageBatchRef.current = appendMonitorMessageBatch;
        setHasSelectedBoardRef.current = setHasSelectedBoard;
        hasSelectedBoardRef.current = hasSelectedBoard;
        selectedBoardRef.current = selectedBoard;
        onIdentifyMismatchRef.current = onIdentifyMismatch;
        onChipIdResolvedRef.current = onChipIdResolved;
    }, [
        appendLog,
        appendErrorLog,
        onBoardsDetected,
        onPortUpdate,
        onUploadEvent,
        appendMonitorMessageBatch,
        setHasSelectedBoard,
        hasSelectedBoard,
        selectedBoard,
        onIdentifyMismatch,
        onChipIdResolved,
    ]);

    useEffect(() => {
        bcRef.current = new BroadcastChannel('schoomy-board');
        return () => {
            bcRef.current?.close();
        };
    }, []);

    const sendMonitorMessageBatch = useCallback((lines: string[]) => {
        appendMonitorMessageBatchRef.current?.(lines);
        bcRef.current?.postMessage({ type: 'webserial_data', lines });
    }, []);

    const sendMonitorMessage = useCallback((message: string) => {
        appendMonitorMessageRef.current?.(message);
        bcRef.current?.postMessage({ type: 'webserial_message', message });
    }, []);

    const disconnect = useCallback(async () => {
        try {
            // モニター読み取りループに「もう続けるな」と明示的に伝え、そのループ自身の後片付け
            // (portLockRef経由のclose含む)が終わるまで待ってから、こちらでも念のためcloseする。
            // これをやらないと、readerだけcancelしてもループが同じポートに新しいreaderを取りに
            // 行ってしまい、下のportRef.current.close()と競合してタブごと固まることがあった
            // (実際に発生した不具合)。
            isMonitorReadingRef.current = false;
            if (readerRef.current) {
                await readerRef.current.cancel().catch(() => {});
                readerRef.current = null;
            }
            await portLockRef.current.catch(() => {});
            if (portRef.current) {
                await portRef.current.close().catch(() => {});
                portRef.current = null;
            }
            setPort(null);
            onPortUpdateRef.current(null);
            // detectedBoardsに古いエントリが残っているとisBoardConnectedが誤ってtrueのままになるため、
            // 切断時はここで確実にクリアする
            onBoardsDetectedRef.current([]);
        } catch (error) {
            appendErrorLogRef.current('切断処理中にエラーが発生しました');
        }
    }, []);

    useEffect(() => {
        if (!navigator.serial) return;
        const handleDisconnect = (e: Event) => {
            const disconnectedPort = e.target;
            if (portRef.current === disconnectedPort) {
                // ボード種別(hasSelectedBoard/board)は保持したまま切断状態にする。
                // こうすると「スクーミーブルーボード / 未接続」と表示され、選択画面まで
                // 全リセットされるより分かりやすい(displayUI/statusTextが選択済み+未接続を扱える)
                disconnect();
            }
        };
        navigator.serial.addEventListener('disconnect', handleDisconnect);
        return () => {
            navigator.serial.removeEventListener('disconnect', handleDisconnect);
        };
    }, [disconnect]);

    // ROM経由のchip_id取得は間欠的に失敗することが実機で確認されている(ESP32のリセット
    // タイミング依存のため)。フル再接続をしなくても、開いたままのポートに対してこれだけを
    // 再試行できるようにする。connect()内の初回取得と、ユーザーが手動で押す再試行ボタンの
    // 両方から呼ぶ。ここではストア(画面表示)への反映は行わない。1回の試行が失敗しても
    // すぐ画面に反映すると、自動リトライで直る場合に一瞬「unknown」が画面にちらつくため、
    // 呼び出し元(connect()/retryChipId)が「最終結果が出てから」まとめて反映する。
    const probeChipIdOnce = useCallback(async (targetPort: SerialPort) => {
        return await readChipIdViaRom(targetPort);
    }, []);

    const retryChipId = useCallback(async () => {
        if (!portRef.current || selectedBoardRef.current !== 'blue') return;
        setIsChipIdProbing(true);
        try {
            const romResult = await probeChipIdOnce(portRef.current);
            onChipIdResolvedRef.current?.(romResult?.chipId ?? null);
        } finally {
            setIsChipIdProbing(false);
        }
    }, [probeChipIdOnce]);

    const connect = useCallback(
        async (baud: number, board: 'orange' | 'blue') => {
            try {
                // ボード種別を切り替える場合、許可済みの全ポートの許可を明示的に破棄してから次を
                // 要求する。オレンジ/ブルーはUSBのVID:PIDが同一(共にCP210x系USB-シリアル変換チップ)
                // で、さらに個体によってはiSerialNumber(USBシリアル番号)まで工場出荷時デフォルトの
                // まま重複している疑いがある。その場合Chrome側のWeb Serial権限/デバイス紐付けが
                // 完全に同一キーとして扱われ、間欠的にROM接続が失敗する(実機で複数回確認済み)。
                // 直前の1ポートだけでなく、getPorts()で列挙できる許可済み全ポートを対象にする。
                const isBoardTypeSwitch = portRef.current !== null && selectedBoardRef.current !== board;
                await disconnect();
                if (isBoardTypeSwitch) {
                    const grantedPorts = await navigator.serial.getPorts().catch(() => [] as SerialPort[]);
                    // 未対応ブラウザではforget自体が存在しないため、必須呼び出しでTypeErrorに
                    // ならないようoptional chainingでガードする。
                    await Promise.all(grantedPorts.map((p) => p.forget?.().catch(() => {})));
                }
                const selectedPort = await navigator.serial.requestPort();
                baudRateRef.current = baud;
                // selectedBoardRef.currentはuseEffect経由でしか更新されないため、モーダル選択直後の
                // 呼び出しではReactの再レンダリング前で古い値を参照するレースの余地がある。
                // 呼び出し元(モーダルのonChange)が持つ確定値をそのまま引数で受け取り、それだけを使う。
                selectedBoardRef.current = board;

                // モーダルで「オレンジ」を選んでいても実機がIoT系ボード(ブルー等)の可能性があるため、
                // portRef/state(=接続確定)に触れる前にROMレベルで実機を確認する。
                // 逆方向(IoT系選択+実機オレンジ)は無応答が正常系と区別できないためスコープ外。
                // (検証済み: この処理を無効化しても「オレンジ→ブルー」の不具合は再現するため、
                // これ自体は原因ではない。)
                if (board === 'orange') {
                    // 数百ms程度で終わる想定だが、無くても画面が固まったように見えるため、
                    // ブルー側のchip_id取得と同じisChipIdProbingでローディング表示を出す。
                    setIsChipIdProbing(true);
                    let isIotBoard: boolean;
                    try {
                        isIotBoard = await isPhysicallyIotBoard(selectedPort);
                    } finally {
                        setIsChipIdProbing(false);
                    }
                    if (isIotBoard) {
                        onIdentifyMismatchRef.current?.();
                        return;
                    }
                } else {
                    // ROM経由(ファームウェア不問)でchip_idを取得する。実機で
                    // scmUtils.getChipId(ESP.getEfuseMac())の値と一致することを確認済み。
                    // 工場出荷直後で一度もfoxから書き込んでいないボードでも、ROMは不変領域なので
                    // 必ず読める(以前のSCMY_ID?方式はアプリファームウェアの応答に依存していたため、
                    // 書き込み済みかどうか・再起動中かどうかに左右される脆い方式だった)。
                    //
                    // requestPort()直後の「まだ一度も触っていないポート」に対する最初の1回だけが
                    // 間欠的に失敗しやすく、同じポートに対してもう一度実行すると成功することを
                    // 実機で複数回確認している(unknownバッジの手動再試行ボタンで復帰するのと同じ
                    // 経路)。原因(Chrome/OS側のポート初回アクセス時の何らかの不安定さ)は特定できて
                    // いないが、実証済みの回復パターンなので自動化する。
                    // 初回失敗を画面に一瞬映さないよう、最終結果が出るまでonChipIdResolvedは呼ばない。
                    setIsChipIdProbing(true);
                    try {
                        let romResult = await probeChipIdOnce(selectedPort);
                        if (!romResult) {
                            romResult = await probeChipIdOnce(selectedPort);
                        }
                        onChipIdResolvedRef.current?.(romResult?.chipId ?? null);
                    } finally {
                        setIsChipIdProbing(false);
                    }
                }

                portRef.current = selectedPort;
                setPort(selectedPort);
                appendLogRef.current(
                    `${board === 'blue' ? 'スクーミーブルーボード' : 'スクーミーオレンジボード'}に接続しました。`,
                );

                const displayPortName = 'webSerialApi';
                const boardInfo: DetectedBoard = {
                    type: board,
                    port: displayPortName,
                };
                if (hasSelectedBoardRef.current) {
                    onBoardsDetectedRef.current([boardInfo]);
                }
                if (onPortUpdateRef.current) {
                    onPortUpdateRef.current(displayPortName);
                }
            } catch (error) {
                appendErrorLogRef.current(`接続エラー: ${error instanceof Error ? error.message : String(error)}`);
            }
        },
        [disconnect],
    );

    useEffect(() => {
        if (!port || !isMonitorOpen || isUploading) return;

        isMonitorReadingRef.current = true;
        let isOpened = false;

        const startReading = async () => {
            await portLockRef.current.catch(() => {});
            try {
                await port.open({ baudRate: baudRateRef.current });
                isOpened = true;
                sendMonitorMessage('☀️ シリアル通信を開始しました');
                let buffer = '';
                const decoder = new TextDecoder();
                const batchedLines: string[] = [];
                while (port.readable && isMonitorReadingRef.current) {
                    const reader = port.readable.getReader();
                    readerRef.current = reader;
                    try {
                        while (true) {
                            const { value, done } = await reader.read();
                            if (done) break;

                            buffer += decoder.decode(value, { stream: true });
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || '';
                            const validLines = lines.map((l) => l.trim()).filter(Boolean);
                            if (validLines.length > 0) {
                                batchedLines.push(...validLines);
                                sendMonitorMessageBatch(batchedLines);
                                batchedLines.length = 0;
                            }
                        }
                    } catch (error: unknown) {
                        if (!isMonitorReadingRef.current) break;
                        let isBreakError = false;
                        if (error instanceof Error || error instanceof DOMException) {
                            isBreakError = error.name === 'BreakError' || error.message.includes('Break');
                        }
                        if (!isBreakError) {
                            sendMonitorMessage(`⛈️ シリアル通信でエラーが発生しました`);
                            break;
                        }
                    } finally {
                        try {
                            reader.releaseLock();
                        } catch (e) {}
                        readerRef.current = null;
                    }
                }

                if (buffer.trim()) {
                    sendMonitorMessageBatch([buffer.trim()]);
                }
            } catch (error) {
                sendMonitorMessage(`⛈️ シリアル通信の開始に失敗しました`);
            }
        };
        portLockRef.current = startReading();
        return () => {
            isMonitorReadingRef.current = false;
            if (readerRef.current) {
                readerRef.current.cancel().catch(() => {});
            }
            portLockRef.current = portLockRef.current.finally(async () => {
                // port.open()自体が失敗した場合はisOpenedがfalseのままなので、
                // 「開始に失敗しました」の直後に矛盾する「停止しました」を出さない。
                if (isOpened) {
                    await port.close().catch(() => {});
                    sendMonitorMessage('🌙 シリアル通信を停止しました');
                }
            });
        };
    }, [port, isMonitorOpen, isUploading, sendMonitorMessageBatch, sendMonitorMessage]);

    const writeToSerial = useCallback(
        async (message: string) => {
            try {
                const writer = portRef.current.writable.getWriter();
                const encoder = new TextEncoder();
                const data = encoder.encode(message + '\n');
                await writer.write(data);
                writer.releaseLock();
            } catch (error) {
                sendMonitorMessage(`🌩️ データの送信に失敗しました: ${error instanceof Error ? error.message : String(error)}`);
            }
        },
        [sendMonitorMessage],
    );

    const webSerialApiUpload = useCallback(
        async (firmwareData: string, firmwareType: 'hex' | 'bin', esp32Artifacts: ESP32Artifact[] | null) => {
            const currentPort = portRef.current;
            if (!currentPort) {
                appendLogRef.current('⚠️スクーミーボードが接続されていません');
                return;
            }
            // disconnect()と同じ手順で止める。モニター読み取りループが生きたままcloseすると、
            // ループが同じポートに新しいreaderを取りに行きport.close()と競合してタブごと
            // 固まる不具合が過去に発生している(disconnect()のコメント参照)。
            isMonitorReadingRef.current = false;
            if (readerRef.current) {
                await readerRef.current.cancel().catch(() => {});
                readerRef.current = null;
            }
            await portLockRef.current.catch(() => {});
            await currentPort.close().catch(() => {});
            await WebSerialApiUpload({
                board: selectedBoardRef.current,
                port: portRef.current,
                firmwareData,
                firmwareDataType: firmwareType,
                esp32Artifacts,
                appendLog: appendLogRef.current,
                onUploadEvent: onUploadEventRef.current,
            });
        },
        [],
    );

    return {
        connect,
        disconnect,
        writeToSerial,
        webSerialApiUpload,
        retryChipId,
        isChipIdProbing,
    };
}
